import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useUser } from '../../context/UserContext';
import { uniqueChannelName } from '../../lib/realtime';

export function useThreadSubscription(threadId: string | null) {
  const { user } = useUser();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Fetch current subscription status
  const fetchSubscription = useCallback(async () => {
    if (!threadId || !user) {
      setIsSubscribed(false);
      setIsMuted(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('chat_thread_subscriptions')
        .select('muted')
        .eq('message_id', threadId)
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setIsSubscribed(true);
        setIsMuted(data.muted);
      } else {
        setIsSubscribed(false);
        setIsMuted(false);
      }
    } catch (err) {
      console.error('Error fetching thread subscription:', err);
      setIsSubscribed(false);
      setIsMuted(false);
    }
  }, [threadId, user]);

  // Subscribe to the thread
  const subscribe = useCallback(async () => {
    if (!threadId || !user) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('chat_thread_subscriptions')
        .insert({
          message_id: threadId,
          user_id: user.id,
          muted: false,
        })
        .select()
        .single();

      if (error) throw error;

      setIsSubscribed(true);
      setIsMuted(false);
    } catch (err) {
      console.error('Error subscribing to thread:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [threadId, user]);

  // Unsubscribe from the thread
  const unsubscribe = useCallback(async () => {
    if (!threadId || !user) return;

    setLoading(true);
    try {
      const { error } = await supabase
        .from('chat_thread_subscriptions')
        .delete()
        .eq('message_id', threadId)
        .eq('user_id', user.id);

      if (error) throw error;

      setIsSubscribed(false);
      setIsMuted(false);
    } catch (err) {
      console.error('Error unsubscribing from thread:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [threadId, user]);

  // Toggle mute status
  const toggleMute = useCallback(async () => {
    if (!threadId || !user) return;

    setLoading(true);
    try {
      const newMuted = !isMuted;

      if (isSubscribed) {
        // Update existing subscription
        const { error } = await supabase
          .from('chat_thread_subscriptions')
          .update({ muted: newMuted })
          .eq('message_id', threadId)
          .eq('user_id', user.id);

        if (error) throw error;
      } else {
        // Create new subscription with muted status
        const { error } = await supabase
          .from('chat_thread_subscriptions')
          .insert({
            message_id: threadId,
            user_id: user.id,
            muted: newMuted,
          })
          .select()
          .single();

        if (error) throw error;
        setIsSubscribed(true);
      }

      setIsMuted(newMuted);
    } catch (err) {
      console.error('Error toggling thread mute:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [threadId, user, isMuted, isSubscribed]);

  // Fetch subscription on thread change
  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  // Realtime subscription for changes to thread subscription
  useEffect(() => {
    if (!threadId || !user) return;

    const subscription = supabase
      .channel(uniqueChannelName(`thread_subscription:${threadId}:${user.id}`))
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'chat_thread_subscriptions',
          filter: `message_id=eq.${threadId}`,
        },
        (payload) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const record = payload.new as any;
          
          if (payload.eventType === 'DELETE' && payload.old?.user_id === user.id) {
            setIsSubscribed(false);
            setIsMuted(false);
          } else if (record?.user_id === user.id) {
            setIsSubscribed(true);
            setIsMuted(record.muted);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, [threadId, user]);

  return {
    isSubscribed,
    isMuted,
    loading,
    toggleMute,
    subscribe,
    unsubscribe,
  };
}